<script setup lang="ts">
import { QTree } from 'quasar'
import { Color } from '@previo/codebooks'
import { computed, onMounted, ref, watch } from 'vue'
import { $t } from '../../../../composables/i18n'
import { PCardRound } from '../../../card/types'
import { PSpaceSize } from '../../../space/types'
import { PMaxLines } from '../../../typography/types'
import PCard from '../../../card/PCard.vue'
import PIcon from '../../../icon/PIcon.vue'
import PSpace from '../../../space/PSpace.vue'
import PTextInputField from '../../../fields/PTextInputField.vue'
import PTextLink from '../../../typography/links/PTextLink.vue'
import { PSettingItem } from '../PSettingItem'

type Props = {
  items: PSettingItem[]
  currentRoute: string
}

const props = defineProps<Props>()

type Emits = {
  (e: 'go-to-route', evt: Event, route: string): void
}
const emits = defineEmits<Emits>()
const filter = ref<string>('')
const treeComponent = ref<InstanceType<typeof QTree>>()
const expanded = ref<string[]>()

const getActiveItemPath = (items: PSettingItem[]): string[] => {
  for (const item of items) {
    if (item.route === props.currentRoute) {
      return [item.id]
    }

    if (item.children.length > 0) {
      const activeChildPath = getActiveItemPath(item.children)

      if (activeChildPath.length > 0) {
        return [item.id, ...activeChildPath]
      }
    }
  }

  return []
}

onMounted(() => {
  expanded.value = getActiveItemPath(props.items)
})

const goToRoute = (e: Event, node: PSettingItem) => {
  if (node.route) {
    emits('go-to-route', e, node.route)
  }
}

const filterByName = (node: PSettingItem, searchValue: string): boolean => {
  return node.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1
}

const visibleItems = computed(() => {
  function filterVisible(items: PSettingItem[]): PSettingItem[] {
    return items
      .filter((item) => item.isVisible)
      .map(
        (item) =>
          new PSettingItem(
            item.id,
            item.name,
            item.icon,
            item.route,
            item.isVisible,
            filterVisible(item.children),
          ),
      )
  }

  return filterVisible(props.items)
})

watch(filter, (value) => {
  if (value && value.length > 0 && treeComponent.value) {
    treeComponent.value.expandAll()
  } else if (treeComponent.value) {
    treeComponent.value.collapseAll()
  }
})
</script>

<template>
  <p-text-input-field v-model="filter" :placeholder="$t('Search settings')" cy="search-setting" />
  <p-space :mobile="PSpaceSize.MD" vertical />

  <q-tree
    ref="treeComponent"
    v-model:expanded="expanded"
    :nodes="visibleItems"
    :no-nodes-label="$t('No results')"
    :filter="filter"
    :filter-method="filterByName"
    node-key="id"
    no-connectors
    class="ds-hide-quasar-arrow-icon"
  >
    <template #default-header="arg">
      <div
        class="p-flex p-flex-center p-justify-between p-full-width p-cursor-pointer"
        @click.prevent="(e) => goToRoute(e, arg.node)"
      >
        <template v-if="arg.node.route === currentRoute">
          <p-card
            :color="Color.PRIMARY_TRANSPARENT"
            :round="PCardRound.LG"
            class="ds-setting-tree-icon p-flex p-justify-center p-items-center"
          >
            <p-icon :color="Color.PRIMARY" :icon="arg.node.icon" :size="12" />
          </p-card>
        </template>
        <template v-else>
          <p-card
            :color="Color.GREY_50"
            :round="PCardRound.LG"
            class="ds-setting-tree-icon p-flex p-justify-center p-items-center"
          >
            <p-icon :color="Color.GREY_500" :icon="arg.node.icon" :size="12" />
          </p-card>
        </template>
        <p-space :mobile="PSpaceSize.SM" />
        <p-text-link :text="arg.node.name" :color="Color.GREY_600" :max-lines="PMaxLines.ONE" />
        <p-space :mobile="PSpaceSize.GROW" />

        <p-icon
          v-if="!arg.node.route"
          :icon="arg.expanded ? 'chevron-up' : 'chevron-down'"
          :color="Color.GREY_400"
        />
      </div>
    </template>
  </q-tree>
</template>

<style lang="scss" scoped>
@import '../../../../assets/scss/shared/variables/ds.spacing';

.ds-setting-tree-icon {
  height: 36px;
  width: 36px;
}

.q-tree {
  :deep(> .q-tree__node--parent:first-of-type) {
    margin: 0 !important;
  }
}

:deep(.q-tree__node) {
  margin: $space-xs-mobile 0;
  padding: 0;
}

:deep(.q-tree__arrow) {
  display: none;
}

:deep(.q-tree__children) {
  padding-left: 20px;
}

:deep(.q-tree__node-header) {
  margin: 0;
  padding: 0 !important;
}
</style>
